import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "@tanstack/react-router";
import { Activity, ArrowLeft, ChevronsLeft, ChevronsRight, HelpCircle } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import type { Concept } from "@/types/concept";
import type { PaperWithRole } from "@/types/paper";
import {
  computeFunnelFrontier,
  computePhaseStates,
  funnelCounts,
  pathToTab,
  STAGE_SEARCH,
  type FunnelTier,
  type PhaseId,
} from "@/lib/conceptRail";
import { useWhereAmIStore } from "@/stores/useWhereAmIStore";
import { useActivityStore } from "@/stores/activity-store";
import { useActivity } from "@/hooks/useActivity";
import { unreadCount } from "@/lib/activityRows";
import { ConceptPopover } from "./ConceptPopover";
import { CurateFunnelNest } from "./CurateFunnelNest";
import { AnalyzeToolNest } from "./AnalyzeToolNest";
import { BuildViewNest } from "./BuildViewNest";

interface Props {
  concept: Concept;
  papers?: PaperWithRole[];
  seedCount: number;
  claimCount: number | null;
  pendingCount: number;
}

const PHASES: { id: PhaseId; to: string }[] = [
  { id: "Curate", to: "/concepts/$conceptId/curate" },
  { id: "Analyze", to: "/concepts/$conceptId/analyze" },
  { id: "Build", to: "/concepts/$conceptId/build" },
];

const DOT_CLASS: Record<string, string> = {
  active: "bg-emerald-400 shadow-[0_0_8px_rgba(166,227,161,0.6)]",
  done: "bg-blue-400",
  default: "bg-muted-foreground/50",
};

const RAIL_COLLAPSED_KEY = "paperchase-rail-collapsed";
const NARROW_QUERY = "(max-width: 900px)";

/** localStorage can throw (private mode / blocked storage); never let it crash the rail. */
function readCollapsedPref(): boolean {
  try {
    return localStorage.getItem(RAIL_COLLAPSED_KEY) === "1";
  } catch {
    return false;
  }
}
function writeCollapsedPref(v: boolean): void {
  try {
    localStorage.setItem(RAIL_COLLAPSED_KEY, v ? "1" : "0");
  } catch {
    /* storage unavailable — collapse still works for the session */
  }
}
function isNarrowViewport(): boolean {
  return typeof window !== "undefined" && !!window.matchMedia && window.matchMedia(NARROW_QUERY).matches;
}

export function ConceptRail({ concept, papers, seedCount, claimCount, pendingCount }: Props) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const tab = pathToTab(pathname);
  const phaseStates = computePhaseStates(tab);
  const counts = funnelCounts(papers ?? [], claimCount);
  const frontier = computeFunnelFrontier(counts);
  // Activity panel lives in the operation bar, which Curate and Analyze mount.
  const hasActivity = tab === "Curate" || tab === "Analyze";
  const activity = useActivity(hasActivity ? concept.id : undefined, "passive").data;
  const activitySeenAt = useActivityStore((s) => s.seenAt[concept.id]);
  const activityUnread = unreadCount(activity, activitySeenAt);

  // Manual intent is persisted; viewport-narrowing forces a collapse WITHOUT
  // overwriting the persisted preference, so widening restores the user's choice.
  const [manualCollapsed, setManualCollapsed] = useState<boolean>(readCollapsedPref);
  const [narrow, setNarrow] = useState<boolean>(isNarrowViewport);
  useEffect(() => {
    writeCollapsedPref(manualCollapsed);
  }, [manualCollapsed]);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia(NARROW_QUERY);
    const on = () => setNarrow(mq.matches);
    on();
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  const collapsed = manualCollapsed || narrow;

  const onTierClick = (tier: FunnelTier) => {
    navigate({
      to: "/concepts/$conceptId/curate",
      params: { conceptId: concept.id },
      search: STAGE_SEARCH[tier],
    });
  };

  if (collapsed) {
    return (
      <aside className="flex w-[46px] shrink-0 flex-col items-center gap-3 pt-2">
        <button
          onClick={() => setManualCollapsed(false)}
          title="Expand sidebar"
          aria-label="Expand sidebar"
          className="flex size-6 items-center justify-center rounded-full border bg-background text-muted-foreground shadow-sm transition-colors hover:bg-accent hover:text-foreground"
        >
          <ChevronsRight className="size-3" />
        </button>
        <div
          className="max-h-[180px] overflow-hidden font-bold text-foreground"
          style={{ writingMode: "vertical-rl", transform: "rotate(180deg)", fontSize: 13, letterSpacing: "0.4px" }}
          title={concept.name}
        >
          {concept.name}
        </div>
        <div className="mt-1 flex flex-col items-center gap-3">
          {PHASES.map(({ id, to }) => (
            <Link
              key={id}
              to={to}
              params={{ conceptId: concept.id }}
              title={id}
              className="size-[9px] rounded-full"
              activeProps={{ style: { background: "#a6e3a1", boxShadow: "0 0 8px rgba(166,227,161,0.6)" } }}
              inactiveProps={{ style: { background: "rgba(127,132,156,0.6)" } }}
            />
          ))}
        </div>
        {/* Build's views have no in-page switcher (Piece C removed the
            toggle), so the collapsed strip must keep them reachable.
            Analyze tools stay center-reachable via the Overview; the
            Curate funnel is informational and safe to hide. */}
        {tab === "Build" && (
          <div className="mt-1 border-t border-border/60 pt-2">
            <BuildViewNest conceptId={concept.id} iconOnly />
          </div>
        )}
        <button
          onClick={() => useWhereAmIStore.getState().open()}
          data-tour-target="whats-next"
          title="What's next?"
          aria-label="What's next?"
          className="mt-auto flex size-6 items-center justify-center text-muted-foreground hover:text-foreground"
        >
          <HelpCircle className="size-3.5" style={{ color: "#94e2d5" }} />
        </button>
      </aside>
    );
  }

  return (
    <aside className="group/rail relative flex w-[224px] shrink-0 flex-col gap-1 pt-1">
      <button
        onClick={() => setManualCollapsed(true)}
        title="Collapse sidebar"
        aria-label="Collapse sidebar"
        className="absolute -right-3 top-1 z-10 flex size-6 items-center justify-center rounded-full border bg-background text-muted-foreground opacity-0 shadow-sm transition-all hover:bg-accent hover:text-foreground group-hover/rail:opacity-100"
      >
        <ChevronsLeft className="size-3" />
      </button>
      <Link
        to="/dashboard"
        className="flex items-center gap-1 px-1 pb-2 text-[10px] text-muted-foreground transition-colors hover:text-foreground"
      >
        <ArrowLeft className="size-3" /> All concepts
      </Link>

      {/* Concept header (the frame) */}
      <Popover>
        <PopoverTrigger asChild>
          <button
            className="mb-1 rounded-lg border px-2.5 py-2 text-left transition-colors hover:bg-accent/40"
            style={{ borderColor: "rgba(148,226,213,0.35)", background: "linear-gradient(180deg,rgba(148,226,213,0.10),transparent)" }}
            title={concept.name}
          >
            <div className="font-mono text-[8px] uppercase tracking-[1.5px]" style={{ color: "#94e2d5" }}>
              Concept
            </div>
            <div className="mt-0.5 line-clamp-2 text-[13px] font-bold leading-tight text-foreground">
              {concept.name}
            </div>
            <div className="mt-1.5 text-[10px] text-muted-foreground">
              <b style={{ color: "#f5c2e7" }}>{seedCount}</b> seed{seedCount === 1 ? "" : "s"} · ✎
            </div>
          </button>
        </PopoverTrigger>
        <PopoverContent className="max-h-[80vh] w-80 overflow-y-auto p-3" align="start" side="right">
          <ConceptPopover concept={concept} paperCount={papers?.length} seedCount={seedCount} />
        </PopoverContent>
      </Popover>

      {/* Phase spine */}
      <div data-tour-target="workflow-tabs" className="flex flex-col gap-1">
      {PHASES.map(({ id, to }) => {
        const state = phaseStates[id];
        return (
          <div key={id}>
            <Link
              to={to}
              params={{ conceptId: concept.id }}
              className="flex items-center gap-2 rounded-md px-2 py-2 text-[13px] text-muted-foreground transition-colors hover:bg-accent/40"
              activeProps={{ className: "bg-accent font-semibold text-foreground" }}
            >
              <span className={`size-[7px] shrink-0 rounded-full ${DOT_CLASS[state]}`} />
              {id}
              {id === "Build" && pendingCount > 0 && (
                <span
                  className="ml-auto inline-flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-bold text-white"
                  style={{ background: "#f59e0b" }}
                >
                  {pendingCount}
                </span>
              )}
            </Link>
            {id === "Curate" && tab === "Curate" && (
              <CurateFunnelNest counts={counts} frontier={frontier} onTierClick={onTierClick} />
            )}
            {id === "Analyze" && tab === "Analyze" && (
              <AnalyzeToolNest conceptId={concept.id} />
            )}
            {id === "Build" && tab === "Build" && (
              <BuildViewNest conceptId={concept.id} />
            )}
          </div>
        );
      })}
      </div>

      <button
        onClick={() => useWhereAmIStore.getState().open()}
        data-tour-target="whats-next"
        className="mt-2 flex items-center justify-center gap-1.5 rounded-md border border-dashed px-2 py-1.5 text-[11px] transition-colors hover:bg-accent/40"
        style={{ color: "#94e2d5", borderColor: "#45475a" }}
      >
        <HelpCircle className="size-3.5" /> What's next?
      </button>
      {hasActivity && (
        <button
          onClick={() => useActivityStore.getState().openPanel(concept.id)}
          className="mt-1.5 flex items-center justify-center gap-1.5 rounded-md border border-dashed px-2 py-1.5 text-[11px] text-muted-foreground transition-colors hover:bg-accent/40"
          style={{ borderColor: "#45475a" }}
        >
          <Activity className="size-3.5" /> Activity
          {activityUnread > 0 && (
            <span className="rounded-full bg-red-400 px-1.5 text-[10px] font-semibold text-slate-900">{activityUnread}</span>
          )}
        </button>
      )}
    </aside>
  );
}
